import React, { useState } from 'react';
import { ChevronDown, HelpCircle, Briefcase, Car } from 'lucide-react';

const ADVERTISER_FAQS = [
  {
    q: 'How is the impression count calculated?',
    a: 'Every active driver runs the Advecti driver app while on the road. We estimate impressions based on the route the driver took, the population density of each area they drove through, and the time they spent in that area. Impressions are then split into "targeted" — the mini-zones you selected during signup — and "non-targeted" — everywhere else inside the I-270 loop that your car was visible.',
  },
  {
    q: 'Who owns the data?',
    a: "You own all aggregated metrics for your campaign — impressions, sessions, integration-pulled revenue/visit data, and any manual entries you upload. We retain only what we need to compute attribution, and you can request deletion at any time from the Data & Privacy tab.",
  },
  {
    q: 'What happens if my driver quits mid-campaign?',
    a: "We assign a replacement driver from our active roster. Your campaign window pauses for the handoff period — typically less than 2 weeks — and resumes once the new driver is wrapped and live. The pause does not count toward your committed campaign duration.",
  },
  {
    q: 'How is the early-termination penalty calculated?',
    a: "Two parts: (1) you pay the full base monthly rate for any remaining months that fall on or before month 3 of the campaign, and (2) you pay back the volume discount for months you've already used. Anything beyond month 3 is forgiven. See the Terms of Service for the worked example, or check the live calculator on the Data & Privacy tab.",
  },
  {
    q: "How are conversions attributed?",
    a: "We compare your performance during the campaign window against a baseline window from 90 days before launch. For each connected integration (Shopify, Square, Stripe, Advecti Pixel, Manual Entry), we compute lift = (current daily average − baseline daily average) / baseline daily average. The Conversions tab shows lift per source.",
  },
  {
    q: 'Why do I need to connect a sales integration?',
    a: "Impressions tell you reach, but conversions tell you ROI. Connecting Shopify, Square, or Stripe and the Advecti Pixel (or manually uploading data) lets us show you the actual sales lift attributed to your campaign.",
  },
  {
    q: 'How long does it take to launch a campaign after I sign up?',
    a: "Typically 5–10 business days. After we approve your lead, we onboard drivers, wrap their vehicles, and the campaign goes live. You'll see drivers populate your Overview tab as they accept assignments, and impressions start flowing once they're on the road.",
  },
  {
    q: 'What happens to my data if I cancel my account?',
    a: "From the Data & Privacy tab you can request immediate deletion. We omit the account and calculate any early-termination penalty (shown in advance), and permanently delete all personal data and integration tokens.",
  },
];

const DRIVER_FAQS = [
  {
    q: 'Is there a cap on daily hours?',
    a: 'No — drivers have no cap on how many hours they can advertise per day. Each campaign comes with a monthly requirement of 100 hours of driving PLUS 100 hours of idle-bubble time. Once BOTH of those hour buckets are filled for a given month, the driver has completed that month and must wait for the next month to start earning again. This means drivers can finish their month faster by driving (and idling in bubble zones) more hours per day.',
  },
  {
    q: 'Are there minimum hour requirements?',
    a: "No — there's no minimum hour requirement, but not completing the full 100 hours in a month leads to an exponential pay decrease. Because pay is tier-scaled (Tier 1: 25% of full rate for the first 30 hrs, Tier 2: 50% for hours 31–60, Tier 3: 100% for hours 61–90, Tier 4: 200% for hours 91–100), the later hours are worth dramatically more than the early ones. A driver who logs 60 hours only picks up Tier 1 and Tier 2 pay and completely misses the two highest-earning tiers. Push through all 100 hours and the last 10 are double-pay — that's where most of the month's earnings actually come from.",
  },
  {
    q: 'How does driver pay work?',
    a: 'Pay is tiered by monthly driving hours (idle-bubble hours are flat and are NOT tier-scaled). Tier 1 (first 30 hrs) pays at 25% of the full rate, Tier 2 (next 30 hrs, hours 31–60) at 50%, Tier 3 (next 30 hrs, hours 61–90) at 100%, and Tier 4 (last 10 hrs, hours 91–100) at 200% (double pay). Full driving rates depend on wrap type and zone — Full Wrap: $8/hr in premium zones, $4/hr in normal (I-270) zones. Partial Wrap: $5/hr in premium zones, $2.50/hr in normal zones.',
  },
  {
    q: "What are 'idle bubbles' and how do they pay?",
    a: 'Idle bubbles are designated high-foot-traffic zones around Columbus — shopping centers, event venues, downtown blocks. When you park your wrapped car inside one, you keep earning at a flat idle rate (idle pay is NOT tiered — it stays the same all month). Full Wrap idle: $1.00/hr premium & $0.50/hr normal during full hours; $0.50/hr premium & $0.25/hr normal during half hours. Partial Wrap idle: $0.60/hr premium & $0.30/hr normal during full hours; $0.30/hr premium & $0.15/hr normal during half hours. Stop for coffee or run an errand in one and your car keeps working for you.',
  },
  {
    q: 'How do I get paid?',
    a: 'You get tracked by the Advectigroup app, which counts your earnings every second as you drive. Payouts go through Branch. When you sign up we issue you a new Branch debit card, and every dollar you earn is loaded onto that card in real time. From there it works like any debit card: swipe it anywhere Mastercard is accepted, pull cash from an ATM, or transfer to your regular bank account.',
  },
  {
    q: 'What kind of car do I need?',
    a: "Any 4-door sedan, SUV, or crossover from 2010 or newer with a clean paint surface and no dents/rust in the wrap area. We handle install and removal for free. If your car has aftermarket paint or a wrap already, contact us and we'll assess it first.",
  },
  {
    q: 'Will the wrap damage my paint?',
    a: 'No. Our vinyl is 3M / Avery-grade, professionally installed and removed. Factory paint in good condition is unaffected.',
  },
  {
    q: 'Do I need to install anything in my car?',
    a: "Yes — but it's minimal. During your wrap install, our installer places a small Bluetooth beacon inside your car (roughly the size of a keyfob, tucked out of sight). The beacon pairs with the Advectigroup app on your phone so we can accurately log your driving hours, idle-bubble time, and impressions. No wiring, no drilling, no drain on your car's battery — the beacon runs on a coin cell that lasts about a year, and we swap it for free at your next wrap touchup.",
  },
  {
    q: 'What happens if I get in an accident?',
    a: "Your personal auto insurance is primary. The wrap itself is easily removable and does not affect your coverage. If you get in an accident, the driver's campaign will be terminated — contact admin immediately and send photos of the damaged car. If the wrap itself is severely damaged, contact admin and we'll repair it. Wrap severity is determined from the triweekly car-wrap photos every driver submits, so we always have a recent baseline to compare against.",
  },
  {
    q: 'Can I drive for rideshare (Uber / Lyft / DoorDash) while wrapped?',
    a: "Rideshare and delivery driving is the ONLY way to drive for Advecti — every mile you log has to come from active gig work like Uber, Lyft, DoorDash, Uber Eats, Instacart, or similar. This keeps you inside our target zones on natural, high-visibility routes instead of empty circling. You keep 100% of what you earn from the rideshare app, and Advecti pays you on top of that for the hours your wrap is on the road.",
  },
  {
    q: 'What if I want to end my campaign early?',
    a: "Talk to us first — we can usually find a solution. If you still want to end early, we do charge a termination fee based on how many months are remaining in your campaign — the more months left, the higher the fee. Contact admin to walk through the exact number before you commit. You keep everything you've already earned.",
  },
  {
    q: 'How do I apply to be a driver?',
    a: (
      <>
        Fill out the driver form on our{' '}
        <a href="/get-started" className="underline font-semibold text-navy hover:text-navy-dark">Get Started</a>{' '}
        page. We review applications weekly and reach out with next steps within 3–5 business days.
      </>
    ),
  },
];

const FAQItem = ({ item, open, onClick, idx, section }) => (
  <div className="border-b border-navy/10" data-testid={`faq-${section}-item-${idx}`}>
    <button
      onClick={onClick}
      className="w-full py-5 flex items-start justify-between gap-4 text-left hover:bg-cream-light/30 px-2 -mx-2 rounded transition-colors"
      data-testid={`faq-${section}-question-${idx}`}
    >
      <span className="font-semibold text-navy text-base sm:text-lg">{item.q}</span>
      <ChevronDown
        className={`text-navy/50 shrink-0 mt-1 transition-transform ${open ? 'rotate-180' : ''}`}
        size={20}
      />
    </button>
    {open && (
      <div className="pb-5 px-2 -mx-2 text-navy/80 leading-relaxed text-sm sm:text-base" data-testid={`faq-${section}-answer-${idx}`}>
        {item.a}
      </div>
    )}
  </div>
);

const FAQ = () => {
  const [tab, setTab] = useState('advertiser');
  const [openIdx, setOpenIdx] = useState(0);

  const list = tab === 'advertiser' ? ADVERTISER_FAQS : DRIVER_FAQS;

  const switchTab = (next) => {
    setTab(next);
    setOpenIdx(0);
  };

  return (
    <div className="bg-cream-light min-h-screen pb-16" data-testid="faq-page">
      <section className="bg-navy text-cream py-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-3">
            <HelpCircle size={28} />
            <p className="uppercase text-xs tracking-widest text-cream/70 font-semibold">Frequently Asked</p>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight">
            Everything you need to know before joining Advecti.
          </h1>
          <p className="text-cream/80 mt-3 text-base">
            Don&apos;t see your question?{' '}
            <a href="/contact" className="underline font-semibold hover:text-cream">Visit the Contact page</a> — we usually reply within a few hours.
          </p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
        {/* Tab switcher */}
        <div className="flex bg-white rounded-full p-1.5 shadow-sm border border-navy/10 mb-6 max-w-md mx-auto" data-testid="faq-tabs">
          <button
            onClick={() => switchTab('advertiser')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full font-semibold text-sm transition ${
              tab === 'advertiser' ? 'bg-navy text-cream' : 'text-navy/70 hover:text-navy'
            }`}
            data-testid="faq-tab-advertiser"
          >
            <Briefcase size={16} /> For Advertisers
          </button>
          <button
            onClick={() => switchTab('driver')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full font-semibold text-sm transition ${
              tab === 'driver' ? 'bg-navy text-cream' : 'text-navy/70 hover:text-navy'
            }`}
            data-testid="faq-tab-driver"
          >
            <Car size={16} /> For Drivers
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-navy/5 p-6 sm:p-8 divide-y divide-navy/10">
          {list.map((item, idx) => (
            <FAQItem
              key={`${tab}-${idx}`}
              item={item}
              idx={idx}
              section={tab}
              open={openIdx === idx}
              onClick={() => setOpenIdx(openIdx === idx ? -1 : idx)}
            />
          ))}
        </div>
      </section>
    </div>
  );
};

export default FAQ;
